import React, { useCallback, useContext, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import {
	faCog,
	faDesktop,
	faHashtag,
	faLocationCrosshairs,
	faPlus,
	faRightFromBracket,
	faTheaterMasks,
	faUserPlus,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faRecord } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import cx from "classnames";

import AccountStore from "~/account/AccountStore";
import AppContext from "~/common/AppContext";
import { GenericInvite } from "~/invite";
import { signOut } from "~/lib/auth/index";
import { activateDelegation } from "~/lib/delegation";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import {
	desktopDownloadLink,
	inCalendarMode,
	inVideocallMode,
	isDesktop,
	isDesktopApp,
	isMacos,
	isWindows,
	isZoom,
	millisecondsToHumanizedDate,
	withQueryParams,
} from "~/lib/utils";
import { NavContext, type PanelKey, SecondaryNavButtonTooltip } from "~/nav";
import { useTrackersFeatureFlagOn } from "~/trackers/featureFlag";
import { AccountAvatar, Avatar, Tooltip } from "~/ui";
import Menu from "~/ui/menu";
import { __globalModalContext } from "~/ui/modals";

import { useCopilotPaywallsEnabled } from "../meetingRecorder/featureFlag";
import RecordLiveMeetingModal from "../recordings/RecordLiveMeetingModal";
import OutOfCreditsModal from "../stream/NoteHeader/OutOfCreditsModal";

import type {
	NavbarAccountDropdown_viewer$data as NavbarAccountDropdownViewer,
	NavbarAccountDropdown_viewer$key as ViewerKey,
} from "~/__generated__/NavbarAccountDropdown_viewer.graphql";

import styles from "./NavbarAccountDropdown.module.css";

type Delegation = NavbarAccountDropdownViewer["delegators"][number];

interface Props {
	viewer: ViewerKey;
}

export default React.memo(NavbarAccountDropdown);

export const navbarAccountDropdownFrag = graphql`
	fragment NavbarAccountDropdown_viewer on Viewer {
		hasAdminAccess
		hasAccessToCopilot
		account {
			id
			name
			logo
			allowInviting
			billingAccount {
				subscriptionLabel
				product {
					family
				}
				hasAccessToAnalytics: hasAccessToFeature(feature: analytics)
			}
		}
		viewer {
			lastUsedDesktopApp
			remainingMeetingRecordingCredits
			...featureFlag_useCopilotPaywallsOn_viewer
		}
		user {
			id
			fullName
			image
			email
			isGuest
			type
			internalAttributes {
				isAccountAdmin
				isManager
			}
		}
		delegators {
			id
			delegator {
				id
				fullName
				image
			}
			expiryDate
		}
		activeDelegation {
			id
			delegator {
				id
				fullName
				email
				image
			}
			delegate {
				id
				fullName
				email
				image
			}
		}
	}
`;

function delegationNotExpired(delegation: Delegation): delegation is NonNullable<Delegation> {
	if (!delegation) {
		return false;
	}
	if (delegation.expiryDate == null) {
		return true;
	}
	return Date.parse(delegation.expiryDate) > Date.now();
}

function NavbarAccountDropdown({ viewer: viewerKey }: Props) {
	const { viewer, user, hasAdminAccess, hasAccessToCopilot, account, delegators, activeDelegation } = useFragment(
		navbarAccountDropdownFrag,
		viewerKey,
	);
	const appContext = useContext(AppContext);
	const openSettingsModal = useCallback(() => appContext.openSettingsModal(), [appContext]);
	const allowDelegatedAccess = appContext.account?.allowDelegatedAccess || false;
	const { dispatchPanel: _dispatchPanel } = useContext(NavContext);
	const hasAccesssToSmartTrackers = useTrackersFeatureFlagOn() && hasAccessToCopilot && !user.isGuest;

	const openPanelFn = useCallback(
		(panelKey: PanelKey) => () => {
			// Don't show the AnalyticsPanel to non-admin users
			if (panelKey === "analytics" && !hasAdminAccess) return;

			_dispatchPanel(panelKey);
			// We want to make sure the panel is opened _after_ navigation has occurred
			// This is a trick that delays the function call just enough
			setTimeout(() => _dispatchPanel("open"), 0);
		},
		[_dispatchPanel, hasAdminAccess],
	);

	const allowAddingWorkspace = !isZoom;

	const nonExpiredDelegators: NonNullable<Delegation>[] = delegators ? delegators.filter(delegationNotExpired) : [];

	const navigateToSignupView = useCallback(() => {
		if (!allowAddingWorkspace) return;

		const rootDomain = window.ROOT_DOMAIN ?? ".fellow.app";
		window.location.href = `https://${rootDomain.slice(1)}/auth/signup/`;
	}, [allowAddingWorkspace]);

	const setInviteModalOpen = useCallback(
		async function setInviteModalOpen() {
			dispatchAnalyticsTrack("[Navbar Account Dropdown] Invite Teammates Clicked");
			if (!account.allowInviting) return;

			return __globalModalContext?.open(({ close }) => (
				<GenericInvite
					onClose={close}
					// eslint-disable-next-line camelcase
					analytics={{ ux_location: "layout__navbar_account_dropdown" }}
				/>
			));
		},
		[account],
	);

	const paywallsEnabled = useCopilotPaywallsEnabled(viewer);

	const setRecordLiveMeetingModalOpen = useCallback(
		async function setInviteModalOpen() {
			// dispatchAnalyticsTrack("[Navbar Account Dropdown] Invite Teammates Clicked");

			const { remainingMeetingRecordingCredits } = viewer;

			const outOfCredits = remainingMeetingRecordingCredits === 0 && paywallsEnabled;
			if (outOfCredits) {
				return __globalModalContext?.open(({ close }) => <OutOfCreditsModal onClose={close} />);
			} else {
				dispatchAnalyticsTrack("Clicked to open record now with URL", { location: "Profile Menu" });
				return __globalModalContext?.open(({ close }) => <RecordLiveMeetingModal onClose={close} />);
			}
		},
		[paywallsEnabled, viewer],
	);

	const downloadDesktopApp = useCallback(() => {
		dispatchAnalyticsTrack("Download Desktop App Clicked", { feature: "Navbar" });
		// eslint-disable-next-line camelcase
		window.open(withQueryParams(desktopDownloadLink, { utm_content: "navbar_account_dropdown" }), "_blank");
	}, []);

	const showAccountSettings = hasAdminAccess;
	const otherAccounts = AccountStore.getOthers();
	const showAccountSwitcher = otherAccounts.length > 0 && !isZoom;
	const { subscriptionLabel, product } = account.billingAccount;

	const accountUser = useMemo(() => ({ name: account.name, image: account.logo || undefined }), [account]);

	const signOutOfFellow = React.useCallback(() => signOut(), []);

	const [isOpen, setOpen] = React.useState(false);
	return (
		<Menu open={isOpen} onOpenChange={setOpen}>
			<SecondaryNavButtonTooltip offset={[0, 8]} content={user.fullName} delay={[1000, 0]} disabled={isOpen}>
				<Menu.Trigger className={styles.trigger}>
					<div className={styles.reveal}>
						<AccountAvatar className={styles.revealInnerFront} account={accountUser} />
						<Avatar className={styles.revealInnerBack} user={user} />
					</div>
				</Menu.Trigger>
			</SecondaryNavButtonTooltip>
			<Menu.Content side="right" className={cx(styles.menuContent, styles.topLevelMenu)}>
				<Menu.Arrow width={20} height={10} />
				{showAccountSwitcher ? (
					<Menu.Sub>
						<Menu.SubTrigger account={accountUser}>
							<div className={styles.text}>
								<strong>{account.name}</strong>
								<span className={cx(styles.plan, styles[product.family])}>{subscriptionLabel}</span>
							</div>
							<small className={styles.subtext}>Switch workspace</small>
						</Menu.SubTrigger>
						<Menu.SubContent className={styles.menuContent}>
							{otherAccounts.length > 0 &&
								otherAccounts.map(otherAccount => (
									<AccountLink
										key={otherAccount.id}
										name={otherAccount.name}
										image={otherAccount.logo}
										uri={otherAccount.uri}
									/>
								))}
							<Menu.Separator />
							<Menu.Item
								key="new-workspace"
								icon={faPlus}
								onSelect={navigateToSignupView}
								disabled={!allowAddingWorkspace}
							>
								<div className={styles.label}>Log in to another workspace</div>
							</Menu.Item>
						</Menu.SubContent>
					</Menu.Sub>
				) : (
					<Menu.Item account={accountUser} onSelect={navigateToSignupView} disabled={!allowAddingWorkspace}>
						<div className={styles.text}>
							<strong>{account.name}</strong>
							<span className={cx(styles.plan, styles[product.family])}>{subscriptionLabel}</span>
						</div>
						{allowAddingWorkspace && <small className={styles.subtext}>Log in to another workspace</small>}
					</Menu.Item>
				)}
				{showAccountSettings && (
					<Menu.ItemLink icon={faCog} to="/account/settings/" onClick={openPanelFn("settings")}>
						Workspace settings
					</Menu.ItemLink>
				)}
				<Menu.Separator />
				<Menu.Item user={user} disabled>
					<span className={styles.userItemText}>{user.fullName}</span>
				</Menu.Item>
				{allowDelegatedAccess && !activeDelegation && nonExpiredDelegators.length >= 1 && (
					<Menu.Sub>
						<Menu.SubTrigger icon={faTheaterMasks} disabled={!allowAddingWorkspace}>
							Delegated Access
						</Menu.SubTrigger>
						<Menu.SubContent className={styles.menuContent}>
							{nonExpiredDelegators.map(delegate => (
								<DelegateEntry
									key={delegate.delegator.id}
									id={delegate.delegator.id}
									name={delegate.delegator.fullName}
									logo={delegate.delegator.image}
									expiryDate={delegate.expiryDate}
								/>
							))}
						</Menu.SubContent>
					</Menu.Sub>
				)}
				<Menu.Item icon={faCog} onSelect={openSettingsModal}>
					User settings
				</Menu.Item>
				<Menu.Separator />
				{!user.isGuest && (
					<>
						<Menu.ItemLink icon={faHashtag} to="/tags/">
							Tags
						</Menu.ItemLink>
						{hasAccesssToSmartTrackers && (
							<Menu.ItemLink icon={faLocationCrosshairs} to="/trackers/">
								Trackers
							</Menu.ItemLink>
						)}
					</>
				)}
				{!user.isGuest && (
					<Tooltip placement="right" content="An admin has disabled sending invites" disabled={!!account.allowInviting}>
						<div>
							<Menu.Item
								icon={faUserPlus}
								iconProps={{ style: { position: "relative", left: "0.125em" } }}
								disabled={!account.allowInviting}
								onSelect={setInviteModalOpen}
							>
								Add teammates
							</Menu.Item>
						</div>
					</Tooltip>
				)}
				{hasAccessToCopilot && (
					<Menu.Item icon={faRecord} onSelect={setRecordLiveMeetingModalOpen}>
						Record now
					</Menu.Item>
				)}
				{isDesktop &&
					!isDesktopApp &&
					!user.isGuest &&
					!inVideocallMode &&
					!inCalendarMode &&
					!viewer.lastUsedDesktopApp && (
						<Tooltip
							placement="right"
							content="Our desktop app is only available on Windows and macOS"
							disabled={!(!isWindows && !isMacos)}
						>
							<div>
								<Menu.Item icon={faDesktop} onSelect={downloadDesktopApp} disabled={!isWindows && !isMacos}>
									Download {isWindows ? "Windows" : "macOS"} app
								</Menu.Item>
							</div>
						</Tooltip>
					)}
				{!isZoom && (
					<Menu.Item icon={faRightFromBracket} destructive onSelect={signOutOfFellow}>
						Sign out of {account.name}
					</Menu.Item>
				)}
			</Menu.Content>
		</Menu>
	);
}

function AccountLink({ name, image, uri }: { name: string; image: string; uri: string }) {
	const accountUser = useMemo(() => ({ name, image: image || undefined }), [name, image]);
	return (
		<Menu.ItemLink href={`${uri}${window.location.pathname}`} showExternalIcon={false} account={accountUser}>
			<div>{name}</div>
			<small className={styles.subtext}>{uri}</small>
		</Menu.ItemLink>
	);
}

function DelegateEntry({
	id,
	name,
	logo,
	expiryDate,
}: {
	id: string;
	name: string;
	logo: string;
	expiryDate: string | null;
}) {
	const logInToDeleteAccount = useCallback(() => {
		activateDelegation(id);
	}, [id]);
	const accountUser = useMemo(() => ({ name: name, image: logo || undefined }), [name, logo]);
	const expiresIn = useMemo<{ expired: boolean; display: string }>(() => {
		if (expiryDate === null) {
			return { expired: false, display: "Never expires" };
		}
		const expiresDate = new Date(Date.parse(expiryDate));
		const currentDate = new Date();

		const nowMS = currentDate.getTime();
		const expiresDateMs = expiresDate.getTime();

		if (expiresDateMs < nowMS) {
			return { expired: true, display: "Expired" };
		}

		const expiresIn = expiresDateMs - nowMS;
		const display = millisecondsToHumanizedDate(expiresIn);

		return { expired: false, display: `Expires in ${display}` };
	}, [expiryDate]);

	return (
		<Menu.Item account={accountUser} onSelect={logInToDeleteAccount}>
			<div>{name}</div>
			<small className={styles.subtext}>{expiresIn.display}</small>
		</Menu.Item>
	);
}
